/**
 * AgentCommandsPanel — comandos remotos del agente (estándar de listados).
 *
 * QUÉ HABÍA ANTES
 * ---------------------------------------------------------------------------
 * Una `<table>` de Bootstrap con `badge bg-*`, un formulario de alta embebido en
 * una tarjeta gris, un `<select>` de Bootstrap para el estado, la salida con
 * fondo oscuro a mano, `alert()` para avisar y botones `btn btn-sm`. Y sin
 * buscador, sin columnas elegibles, sin KPIs ni export.
 *
 * AHORA
 * ---------------------------------------------------------------------------
 * Listado **embebido** (vive dentro de las pestañas de Agente CMDB) con el
 * andamiaje común: buscador, filtros, KPIs que filtran, Lista/Fichas, columnas
 * elegibles y export. El alta va al **diálogo común** (con la ayuda de cada
 * comando dentro, que es donde se lee) y la salida del comando se abre en un
 * diálogo con su salida en un bloque monoespaciado.
 *
 * El resultado puede ser un objeto (`{stdout, stderr, exit_code}`) o una cadena:
 * ambos casos se pintan igual, porque el agente no siempre contesta lo mismo.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BoltIcon,
  ListBulletIcon,
  Squares2X2Icon,
  ArrowPathIcon,
  PlusIcon,
  DocumentTextIcon,
  SignalSlashIcon,
} from '@heroicons/react/24/outline';
import ListPage from './common/ListPage';
import Dialog from './common/Dialog';
import { docElementPath } from '../utils/docElements';
import { hace } from '../utils/relativeTime';
import api from '../services/api';

const ESTADOS = {
  pending: { label: 'Pendiente', tono: 'list-badge--warn' },
  running: { label: 'Ejecutando', tono: 'list-badge--accent' },
  done: { label: 'Completado', tono: 'list-badge--on' },
  error: { label: 'Error', tono: 'list-badge--off' },
  cancelled: { label: 'Cancelado', tono: 'list-badge--muted' },
};

const COMANDOS = {
  rescan: 'Vuelve a descubrir el equipo (inventario completo).',
  hypervisor_sync: 'Sincroniza el hipervisor y sus máquinas virtuales.',
  exec: 'Ejecuta un comando de la lista de permitidos del agente.',
};

const CI_TIPOS = [
  { value: 'workstation', label: 'Estación de trabajo' },
  { value: 'rack_element', label: 'Servidor' },
  { value: 'virt_vm', label: 'Máquina virtual' },
  { value: 'virt_hypervisor', label: 'Hipervisor' },
];

/** Convierte `result` (objeto o cadena) en texto legible para el diálogo. */
function textoResultado(result) {
  if (result === null || result === undefined) return '';
  if (typeof result === 'string') return result;
  try {
    return JSON.stringify(result, null, 2);
  } catch {
    return String(result);
  }
}

export default function AgentCommandsPanel({
  companyId,
  agentId = null,
  ciType = null,
  ciId = null,
}) {
  const navigate = useNavigate();

  const [commands, setCommands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [aviso, setAviso] = useState('');
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCommand, setFilterCommand] = useState('');
  const [filterAgent, setFilterAgent] = useState('');
  const [filterResult, setFilterResult] = useState('');
  const [groupMode, setGroupMode] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [outputOf, setOutputOf] = useState(null);
  const [form, setForm] = useState({
    command: 'rescan',
    agent_id: agentId || '',
    ci_type: ciType || '',
    ci_id: ciId || '',
    exec_cmd: '',
    hypervisor_type: '',
  });
  const [enqueueing, setEnqueueing] = useState(false);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    try {
      const filtros = {};
      if (agentId) filtros.agent_id = agentId;
      if (ciType) filtros.ci_type = ciType;
      if (ciId) filtros.ci_id = ciId;
      const res = await api.getAgentCommands(companyId, filtros);
      setCommands(res.commands || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [companyId, agentId, ciType, ciId]);

  useEffect(() => { load(); }, [load]);

  const mostrarAviso = (texto) => {
    setAviso(texto);
    setTimeout(() => setAviso(''), 3000);
  };

  const encolar = async () => {
    setEnqueueing(true);
    try {
      await api.enqueueAgentCommand(companyId, {
        command: form.command,
        agent_id: form.agent_id || undefined,
        ci_type: form.ci_type || undefined,
        ci_id: form.ci_id ? parseInt(form.ci_id, 10) : undefined,
        payload: form.command === 'exec'
          ? { cmd: form.exec_cmd }
          : (form.command === 'hypervisor_sync' && form.hypervisor_type)
            ? { hypervisor_type: form.hypervisor_type }
            : {},
      });
      setShowForm(false);
      mostrarAviso('Comando encolado');
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setEnqueueing(false);
    }
  };

  const detectarOffline = async () => {
    try {
      await api.triggerOfflineDetection(companyId);
      mostrarAviso('Detección de offline lanzada: los equipos sin latido reciente quedan marcados');
    } catch (e) {
      setError(e.message);
    }
  };

  /* ── filtrado ───────────────────────────────────────────────────────────── */
  const etiquetaCi = (tipo) => CI_TIPOS.find((t) => t.value === tipo)?.label || tipo || '—';

  const agentes = useMemo(() => {
    const ids = [...new Set(commands.map((c) => c.agent_id).filter(Boolean))];
    return ids.sort().map((id) => ({ value: id, label: id.slice(0, 8) + '…' }));
  }, [commands]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return commands.filter((c) => {
      if (filterStatus && c.status !== filterStatus) return false;
      if (filterCommand && c.command !== filterCommand) return false;
      if (filterAgent && c.agent_id !== filterAgent) return false;
      if (filterResult === 'yes' && !c.result) return false;
      if (filterResult === 'no' && c.result) return false;
      if (!term) return true;
      return [
        c.command,
        c.agent_id,
        c.ci_type ? `${c.ci_type} ${c.ci_id}` : '',
        (ESTADOS[c.status] || {}).label,
        textoResultado(c.result),
      ].filter(Boolean).join(' ').toLowerCase().includes(term);
    });
  }, [commands, search, filterStatus, filterCommand, filterAgent, filterResult]);

  const clearFilters = () => {
    setFilterStatus(''); setFilterCommand(''); setFilterAgent(''); setFilterResult(''); setSearch('');
  };

  const cuenta = useMemo(() => ({
    total: commands.length,
    pendientes: commands.filter((c) => c.status === 'pending').length,
    ejecutando: commands.filter((c) => c.status === 'running').length,
    hechos: commands.filter((c) => c.status === 'done').length,
    errores: commands.filter((c) => c.status === 'error').length,
  }), [commands]);

  const kpis = (
    <div className="list-kpis list-kpis--chips">
      <button type="button" className="list-kpi-chip" onClick={clearFilters} title="Ver todos los comandos">
        <span className="list-kpi-value">{cuenta.total}</span>
        <span className="list-kpi-label">comandos</span>
      </button>
      <button type="button" className="list-kpi-chip"
        onClick={() => { clearFilters(); setFilterStatus('pending'); }} title="Ver los pendientes">
        <span className="list-kpi-value">{cuenta.pendientes}</span>
        <span className="list-kpi-label">pendientes</span>
      </button>
      <button type="button" className="list-kpi-chip"
        onClick={() => { clearFilters(); setFilterStatus('running'); }} title="Ver los que están en curso">
        <span className="list-kpi-value">{cuenta.ejecutando}</span>
        <span className="list-kpi-label">en curso</span>
      </button>
      <button type="button" className="list-kpi-chip"
        onClick={() => { clearFilters(); setFilterStatus('done'); }} title="Ver los completados">
        <span className="list-kpi-value">{cuenta.hechos}</span>
        <span className="list-kpi-label">completados</span>
      </button>
      <button type="button" className="list-kpi-chip"
        onClick={() => { clearFilters(); setFilterStatus('error'); }} title="Ver los que fallaron">
        <span className="list-kpi-value">{cuenta.errores}</span>
        <span className="list-kpi-label">con error</span>
      </button>
    </div>
  );

  /* ── columnas ───────────────────────────────────────────────────────────── */
  const columns = useMemo(() => {
    const dash = <span className="list-hint">—</span>;
    return [
      {
        key: 'status',
        label: 'Estado',
        sortable: true,
        sortValue: (c) => (ESTADOS[c.status] || {}).label || c.status,
        render: (c) => {
          const estado = ESTADOS[c.status] || { label: c.status, tono: 'list-badge--muted' };
          return <span className={`list-badge ${estado.tono}`}>{estado.label}</span>;
        },
      },
      {
        key: 'command',
        label: 'Comando',
        sortable: true,
        render: (c) => (
          <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
            <span className="list-code">{c.command}</span>
            {c.command === 'exec' && c.payload?.cmd && (
              <span className="list-hint list-code">{c.payload.cmd}</span>
            )}
          </span>
        ),
      },
      {
        key: 'agent_id',
        label: 'Agente',
        sortable: true,
        render: (c) => (c.agent_id
          ? <span className="list-code" title={c.agent_id}>{c.agent_id.slice(0, 8)}…</span>
          : dash),
      },
      {
        key: 'ci',
        label: 'Dispositivo',
        sortable: true,
        sortValue: (c) => (c.ci_type ? `${c.ci_type} ${c.ci_id}` : ''),
        render: (c) => {
          if (!c.ci_type) return dash;
          const ruta = docElementPath(c.ci_type, c.ci_id);
          return (
            <button
              type="button"
              className="list-badge"
              style={{ cursor: ruta ? 'pointer' : 'default' }}
              onClick={(event) => { event.stopPropagation(); if (ruta) navigate(ruta); }}
              title={ruta ? 'Ver el dispositivo' : 'Este tipo no tiene pantalla propia'}
            >
              {etiquetaCi(c.ci_type)} #{c.ci_id}
            </button>
          );
        },
      },
      {
        key: 'created_at',
        label: 'Encolado',
        sortable: true,
        render: (c) => (
          <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
            <span>{new Date(c.created_at).toLocaleString()}</span>
            <span className="list-hint">{hace(c.created_at)}</span>
          </span>
        ),
      },
      {
        key: 'executed_at',
        label: 'Ejecutado',
        sortable: true,
        render: (c) => (c.executed_at
          ? (
            <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
              <span>{new Date(c.executed_at).toLocaleString()}</span>
              <span className="list-hint">{hace(c.executed_at)}</span>
            </span>
          )
          : <span className="list-hint">Sin ejecutar</span>),
      },
      {
        key: 'result',
        label: 'Resultado',
        sortable: true,
        sortValue: (c) => (c.result ? 1 : 0),
        render: (c) => (c.result
          ? (
            <button
              type="button"
              className="list-badge"
              onClick={(e) => { e.stopPropagation(); setOutputOf(c); }}
            >
              Ver salida
            </button>
          )
          : <span className="list-hint">—</span>),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const filters = [
    {
      key: 'status', label: 'Estado', allLabel: 'Cualquier estado',
      value: filterStatus, onChange: setFilterStatus,
      options: Object.entries(ESTADOS).map(([value, v]) => ({ value, label: v.label })),
    },
    {
      key: 'command', label: 'Comando', allLabel: 'Cualquier comando',
      value: filterCommand, onChange: setFilterCommand,
      options: Object.keys(COMANDOS).map((c) => ({ value: c, label: c })),
    },
    {
      key: 'agent', label: 'Agente', allLabel: 'Cualquier agente',
      value: filterAgent, onChange: setFilterAgent,
      options: agentes,
    },
    {
      key: 'result', label: 'Resultado', allLabel: 'Con o sin salida',
      value: filterResult, onChange: setFilterResult,
      options: [
        { value: 'yes', label: 'Con salida' },
        { value: 'no', label: 'Sin salida todavía' },
      ],
    },
    {
      key: 'group', label: 'Agrupar por', allLabel: 'Sin agrupar',
      value: groupMode, onChange: setGroupMode,
      options: [
        { value: 'status', label: 'Estado' },
        { value: 'command', label: 'Comando' },
        { value: 'agent', label: 'Agente' },
      ],
    },
  ];

  const groupBy = useMemo(() => {
    if (groupMode === 'status') return (c) => (ESTADOS[c.status] || {}).label || c.status;
    if (groupMode === 'command') return (c) => c.command;
    if (groupMode === 'agent') return (c) => c.agent_id || 'Sin agente';
    return undefined;
  }, [groupMode]);

  const exportRows = useMemo(() => filtered.map((c) => ({
    'Estado': (ESTADOS[c.status] || {}).label || c.status,
    'Comando': c.command,
    'Parámetros': c.payload?.cmd || c.payload?.hypervisor_type || '',
    'Agente': c.agent_id || '',
    'Dispositivo': c.ci_type ? `${etiquetaCi(c.ci_type)} #${c.ci_id}` : '',
    'Encolado': c.created_at ? new Date(c.created_at).toLocaleString() : '',
    'Ejecutado': c.executed_at ? new Date(c.executed_at).toLocaleString() : '',
    'Salida': textoResultado(c.result).slice(0, 500),
  })), [filtered]);

  return (
    <>
      <ListPage
        icon={BoltIcon}
        title="Comandos remotos"
        subtitle={`${commands.length} comandos · ${filtered.length} filtrados`}
        embedded

        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar por comando, agente o dispositivo…"
        filters={filters}

        exportRows={exportRows}
        exportFileBase="comandos-agente"
        exportSheetName="Comandos"

        columns={columns}
        columnStorageKey="cmdb_cols_agent_commands"
        defaultVisible={['status', 'command', 'agent_id', 'ci', 'created_at']}
        rows={filtered}
        rowKey={(c) => c.id}
        rowTitle={(c) => `${c.command} · ${(ESTADOS[c.status] || {}).label || c.status}`}
        groupBy={groupBy}

        kpis={kpis}

        onCreate={() => setShowForm(true)}
        createLabel="Encolar comando"
        createTitle="Pide un comando a un agente"

        headerActions={(
          <>
            {aviso && <span className="list-badge list-badge--on">{aviso}</span>}
            <button type="button" className="list-btn-secondary" onClick={load} disabled={loading}>
              <ArrowPathIcon style={{ width: 15, height: 15 }} /> Actualizar
            </button>
            <button type="button" className="list-btn-secondary" onClick={detectarOffline}>
              <SignalSlashIcon style={{ width: 15, height: 15 }} /> Detectar offline
            </button>
          </>
        )}

        views={[
          { key: 'list', label: 'Lista', icon: ListBulletIcon, title: 'Tabla con todas las columnas' },
          {
            key: 'card', label: 'Fichas', icon: Squares2X2Icon,
            title: 'Tarjetas con lo esencial de cada comando',
            render: ({ rows, rowActions }) => (
              <div className="list-cards">
                {rows.map((c) => {
                  const estado = ESTADOS[c.status] || { label: c.status, tono: 'list-badge--muted' };
                  return (
                    <div key={c.id} className="list-card">
                      <div className="list-card-head">
                        <div>
                          <div className="list-card-title list-code">{c.command}</div>
                          <span className="list-hint">{hace(c.created_at)}</span>
                        </div>
                        <span className={`list-badge ${estado.tono}`}>{estado.label}</span>
                      </div>
                      <div className="list-card-meta">
                        {c.agent_id && (
                          <span className="list-code" title={c.agent_id}>{c.agent_id.slice(0, 8)}…</span>
                        )}
                        {c.ci_type && <span className="list-badge">{etiquetaCi(c.ci_type)} #{c.ci_id}</span>}
                        {c.payload?.cmd && <span className="list-hint list-code">{c.payload.cmd}</span>}
                      </div>
                      <div className="list-card-actions">
                        {c.result && (
                          <button
                            type="button"
                            className="list-action-btn"
                            onClick={() => setOutputOf(c)}
                            title="Ver la salida"
                          >
                            <DocumentTextIcon />
                          </button>
                        )}
                        {rowActions(c)}
                      </div>
                    </div>
                  );
                })}
              </div>
            ),
          },
        ]}
        listKey="agent_commands"
        defaultView="list"

        loading={loading}
        loadingText="Cargando comandos…"
        error={error}

        empty={{
          icon: '⚡',
          title: commands.length === 0
            ? 'No hay comandos encolados'
            : 'Ningún comando coincide con los filtros',
          subtitle: commands.length === 0
            ? 'Un comando se encola aquí y el agente lo recoge en su siguiente sondeo (rescan, hypervisor_sync o exec).'
            : undefined,
          action: commands.length === 0 ? (
            <button type="button" className="list-btn-primary" onClick={() => setShowForm(true)}>
              <PlusIcon style={{ width: 14, height: 14 }} /> Encolar comando
            </button>
          ) : undefined,
        }}
      />

      {/* ══ Alta: diálogo común ══ */}
      {showForm && (
        <Dialog
          title="Encolar comando"
          icon={<BoltIcon style={{ width: 18, height: 18 }} />}
          maxWidth={620}
          busy={enqueueing}
          onClose={() => setShowForm(false)}
          footer={(
            <>
              <button
                type="button"
                className="list-btn-secondary"
                onClick={() => setShowForm(false)}
                disabled={enqueueing}
              >
                Cancelar
              </button>
              <button type="button" className="list-btn-primary" onClick={encolar} disabled={enqueueing}>
                {enqueueing ? 'Encolando…' : 'Encolar'}
              </button>
            </>
          )}
        >
          <label className="list-hint" style={{ display: 'block', marginBottom: 10 }}>
            Comando
            <select
              className="list-select"
              style={{ width: '100%', marginTop: 4 }}
              value={form.command}
              onChange={(e) => setForm({ ...form, command: e.target.value })}
            >
              {Object.entries(COMANDOS).map(([value, ayuda]) => (
                <option key={value} value={value}>{value} — {ayuda}</option>
              ))}
            </select>
            <span className="list-hint">{COMANDOS[form.command]}</span>
          </label>

          {form.command === 'exec' && (
            <label className="list-hint" style={{ display: 'block', marginBottom: 10 }}>
              Comando a ejecutar
              <input
                className="list-input"
                style={{ width: '100%', marginTop: 4 }}
                value={form.exec_cmd}
                onChange={(e) => setForm({ ...form, exec_cmd: e.target.value })}
                placeholder="uptime"
              />
              <span className="list-hint">
                El agente solo ejecuta lo que esté en su lista de permitidos (allowed_commands.txt).
              </span>
            </label>
          )}

          {form.command === 'hypervisor_sync' && (
            <label className="list-hint" style={{ display: 'block', marginBottom: 10 }}>
              Tipo de hipervisor
              <select
                className="list-select"
                style={{ width: '100%', marginTop: 4 }}
                value={form.hypervisor_type}
                onChange={(e) => setForm({ ...form, hypervisor_type: e.target.value })}
              >
                <option value="">Detectar automáticamente</option>
                <option value="hyperv">Hyper-V</option>
                <option value="vmware">VMware</option>
              </select>
            </label>
          )}

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <label className="list-hint" style={{ flex: '1 1 220px' }}>
              Agente (UUID)
              <input
                className="list-input"
                style={{ width: '100%', marginTop: 4 }}
                value={form.agent_id}
                onChange={(e) => setForm({ ...form, agent_id: e.target.value })}
                placeholder="Vacío = cualquiera de la empresa"
              />
            </label>
            <label className="list-hint" style={{ flex: '0 1 180px' }}>
              Tipo de dispositivo
              <select
                className="list-select"
                style={{ width: '100%', marginTop: 4 }}
                value={form.ci_type}
                onChange={(e) => setForm({ ...form, ci_type: e.target.value })}
              >
                <option value="">—</option>
                {CI_TIPOS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </label>
            <label className="list-hint" style={{ flex: '0 1 120px' }}>
              ID
              <input
                className="list-input"
                type="number"
                style={{ width: '100%', marginTop: 4 }}
                value={form.ci_id}
                onChange={(e) => setForm({ ...form, ci_id: e.target.value })}
                placeholder="—"
              />
            </label>
          </div>
          <p className="list-hint" style={{ marginBottom: 0 }}>
            El identificador del dispositivo es solo para el rastro de auditoría: quien ejecuta
            el comando es el agente.
          </p>
        </Dialog>
      )}

      {/* ══ Salida del comando ══ */}
      {outputOf && (
        <Dialog
          title={`Salida · ${outputOf.command}`}
          icon={<DocumentTextIcon style={{ width: 18, height: 18 }} />}
          maxWidth={720}
          onClose={() => setOutputOf(null)}
          footer={(
            <button type="button" className="list-btn-secondary" onClick={() => setOutputOf(null)}>
              Cerrar
            </button>
          )}
        >
          <div className="list-hint" style={{ marginBottom: 8, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <span className={`list-badge ${(ESTADOS[outputOf.status] || {}).tono || 'list-badge--muted'}`}>
              {(ESTADOS[outputOf.status] || {}).label || outputOf.status}
            </span>
            {outputOf.agent_id && (
              <span className="list-code" title={outputOf.agent_id}>{outputOf.agent_id.slice(0, 8)}…</span>
            )}
            {outputOf.executed_at && <span>Ejecutado {hace(outputOf.executed_at)}</span>}
          </div>
          <pre
            style={{
              margin: 0,
              maxHeight: 340,
              overflow: 'auto',
              padding: '10px 12px',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border)',
              background: 'var(--bg-surface)',
              color: 'var(--text-primary)',
              fontFamily: 'var(--font-mono)',
              fontSize: '0.78rem',
              whiteSpace: 'pre-wrap',
            }}
          >
            {textoResultado(outputOf.result) || 'Sin salida'}
          </pre>
        </Dialog>
      )}
    </>
  );
}
